import { ReactNode, useCallback, useMemo, useRef, useState } from 'react'
import { OmpActionContext } from './ompActions'
import type { ActionMode } from './ompActions'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { InputType, HTTP_STATUS } from '@/constants'
import { useSessionStore, useCartStore, useUIStore } from '@/stores'
import { authService } from '@/services/authService'
import { queryKeys } from '@/services/queryClient'
import { formatErrorMessage } from '@utils'
import { emitProcessingMessages, clearProcessingMessages } from '@/utils/processing'
import { useToast } from '@/hooks/useToast'
import { getInputFieldDefaults } from '@utils/session'
import { findItemSelectionInput, hasRenderableStep } from './utils/portalActions'
import { isSyncedToUrl, resolveCloseAction, resyncPortalSession } from './utils/portalRecovery'
import { restoreStep, returnFunnel, takeFunnel } from './utils/portalSession'

function isSameStep(before: any, after: any): boolean {
  const from = before?.step_id ?? before?.step
  const to = after?.step_id ?? after?.step
  return from != null && to != null && String(from) === String(to)
}

export const OmpActionsProvider = ({ children }: { children: ReactNode }) => {
  const toast = useToast()
  const qc = useQueryClient()
  const navigate = useNavigate()
  const [mode, setMode] = useState<ActionMode | null>(null)
  const [title, setTitle] = useState('')
  const [item, setItem] = useState<any | null>(null)
  const [product, setProduct] = useState<any | null>(null)
  const [exchangeProduct, setExchangeProduct] = useState<any | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const lastParams = useRef<Record<string, any> | null>(null)
  const [actionIntent, setActionIntent] = useState<number | null>(null)
  // Survives startAction's stale closure.
  const actionIntentRef = useRef<number | null>(null)
  const inFlight = useRef(false)

  const close = useCallback(() => {
    setError(null)
    lastParams.current = null
    setMode(null)
    setTitle('')
    setItem(null)
    setProduct(null)
    setExchangeProduct(null)
    setActionIntent(null)
    actionIntentRef.current = null
  }, [])

  // The funnel can land on the list step (`intents: []`) while the URL is a detail page.
  const settleCompleted = useCallback(async (): Promise<boolean> => {
    if (isSyncedToUrl()) {
      returnFunnel()
      return true
    }
    return resyncPortalSession()
  }, [])

  const refreshPortalData = useCallback(() => {
    qc.resetQueries({ queryKey: queryKeys.portal })
  }, [qc])

  // Close first: the global loader should be the only spinner.
  const settleAndRefresh = useCallback(async () => {
    close()
    if (await settleCompleted()) {
      refreshPortalData()
    } else {
      // A stuck pin diverts every later payload.
      returnFunnel()
      navigate('/portal', { replace: true })
    }
  }, [close, navigate, refreshPortalData, settleCompleted])

  const handoverToLifeline = useCallback(() => {
    const live = useSessionStore.getState()
    const lifelineStep = live.popupSession
    returnFunnel()
    if (lifelineStep) {
      useSessionStore.getState().setSession(lifelineStep)
    }
    close()
  }, [close])

  const runInput = useCallback(
    async (params: Record<string, any>) => {
      const store = useSessionStore.getState()
      const session = store.session as any
      if (!session?.id || inFlight.current) {
        return
      }
      inFlight.current = true
      lastParams.current = params
      setError(null)
      setBusy(true)
      emitProcessingMessages(null, null)
      try {
        const result = await authService.input(session.id, params)
        store.setSession(result)

        if (useSessionStore.getState().isLifelineActive()) {
          handoverToLifeline()
          return result
        }

        if (!hasRenderableStep(result)) {
          setBusy(false)
          await settleAndRefresh()
        }
        return result
      } catch (err) {
        const message = formatErrorMessage(err)
        setError(message)
        toast.error(message)
      } finally {
        inFlight.current = false
        clearProcessingMessages()
        setBusy(false)
      }
    },
    [toast, settleAndRefresh, handoverToLifeline]
  )

  const retry = useCallback(() => {
    const params = lastParams.current
    if (params) {
      runInput(params)
    }
  }, [runInput])

  const startAction = useCallback(
    async (actionItem: any, intent: number, actionTitle: string) => {
      const store = useSessionStore.getState()
      const session = store.session as any
      const input = findItemSelectionInput(session)
      if (!input || !actionItem?.id) {
        toast.error('This action is currently unavailable.')
        return
      }
      if (inFlight.current) {
        return
      }
      takeFunnel(session)
      actionIntentRef.current = intent

      const params: Record<string, any> = { input: input.id, item: actionItem.id }
      if (session?.use_intents) {
        params.intent = intent
      }

      const before = session
      inFlight.current = true
      const ui = useUIStore.getState()
      ui.setLoading(true)
      emitProcessingMessages(null, null)
      try {
        const result = await authService.input(session.id, params)
        useSessionStore.getState().setSession(result)

        if (useSessionStore.getState().isLifelineActive()) {
          handoverToLifeline()
          return
        }

        if (!hasRenderableStep(result)) {
          // Same step back = no path for this intent; nothing ran.
          if (isSameStep(before, result)) {
            restoreStep(before)
            close()
            toast.error('This action is currently unavailable.')
            return
          }
          await settleAndRefresh()
          return
        }

        setMode('intent')
        setActionIntent(intent)
        setTitle(actionTitle)
        setItem(actionItem)
        setProduct(null)
      } catch (err) {
        returnFunnel()
        toast.error(formatErrorMessage(err))
      } finally {
        inFlight.current = false
        clearProcessingMessages()
        ui.setLoading(false)
      }
    },
    [close, settleAndRefresh, handoverToLifeline, toast]
  )

  const openCheckout = useCallback(
    async (actionItem: any, t = 'Checkout') => {
      const store = useSessionStore.getState()
      const session = store.session as any
      if (!session?.id || inFlight.current) {
        return
      }
      setMode('checkout')
      setActionIntent(null)
      actionIntentRef.current = null
      setTitle(t)
      setItem(actionItem)
      setProduct(null)
      inFlight.current = true
      setBusy(true)
      emitProcessingMessages(null, null)
      try {
        // This item alone: updateSessionCart replaces the server cart with what's local.
        const cartStore = useCartStore.getState()
        cartStore.setCart([])
        const baseProduct = actionItem.campaign_product ?? actionItem
        // Checkout reads bundles off campaign_product, not the cart row.
        const bundled = actionItem.bundled_variants ?? baseProduct?.bundled_variants
        await cartStore.addItemsToSessionCart(session.id, [
          {
            campaign_product: {
              ...baseProduct,
              ...(bundled?.length ? { bundled_variants: bundled } : {}),
              // campaign_product.product is only an id.
              product: baseProduct?.product ?? actionItem.product,
            },
            variant: actionItem.variant ?? null,
            quantity: actionItem.quantity || 1,
            buy_now_clicked: true,
          } as any,
        ])
        takeFunnel(session)
        const result = await authService.jump(session.id, { step: 'checkout' })
        useSessionStore.getState().setSession(result)
      } catch (err) {
        returnFunnel()
        toast.error(formatErrorMessage(err))
        close()
      } finally {
        inFlight.current = false
        clearProcessingMessages()
        setBusy(false)
      }
    },
    [close, toast]
  )

  const submitCheckout = useCallback(
    async (input: any, params: Record<string, any>) => {
      const store = useSessionStore.getState()
      const sessionId = (store.session as any)?.id
      if (!sessionId || !input?.id || inFlight.current) {
        return
      }
      inFlight.current = true
      setBusy(true)
      emitProcessingMessages(null, null)
      try {
        // Checkout's params are the data payload.
        const data = { ...getInputFieldDefaults(input), ...(params || {}) }
        const result = await authService.input(sessionId, {
          input: input.id,
          ...(Object.keys(data).length ? { data } : {}),
        })
        useCartStore.getState().clearCart()
        useSessionStore.getState().setSession(result)
        close()
        setBusy(false)
        if (!(await resyncPortalSession())) {
          returnFunnel()
          navigate('/portal', { replace: true })
        }
        qc.resetQueries({ queryKey: queryKeys.portal })
        toast.success('Your order is placed.')
      } catch (err: any) {
        // 400 carries per-field validation for the form.
        const errData = err?.response?.data
        if (err?.response?.status === HTTP_STATUS.BAD_REQUEST && errData && typeof errData === 'object') {
          useCartStore.getState().setCheckoutErrors([errData])
        }
        toast.error(formatErrorMessage(err))
      } finally {
        inFlight.current = false
        clearProcessingMessages()
        setBusy(false)
      }
    },
    [close, navigate, qc, toast]
  )

  const openAddProduct = useCallback((p: any, t = 'Add Product') => {
    setMode('add_product')
    setActionIntent(null)
    actionIntentRef.current = null
    setTitle(t)
    setProduct(p)
    setItem(null)
  }, [])

  const submitInput = useCallback(
    (input: any, data?: Record<string, any>, campaignProduct?: any) => {
      if (campaignProduct !== undefined) {
        setExchangeProduct(campaignProduct || null)
      }
      const params: Record<string, any> = { input: input.id }
      if (data && Object.keys(data).length) {
        params.data = { ...getInputFieldDefaults(input), ...data }
      }
      runInput(params)
    },
    [runInput]
  )

  const submitData = useCallback(
    (data: Record<string, any>, mainItem?: any) => {
      const store = useSessionStore.getState()
      const step = (store.popupSession ?? store.session) as any
      const input = (step?.valid_inputs as any[])?.[0]
      if (!input) {
        return
      }
      const params: Record<string, any> = { input: input.id, data: { ...getInputFieldDefaults(input), ...data } }
      if (mainItem?.id != null) {
        params.item = mainItem.id
      }
      runInput(params)
    },
    [runInput]
  )

  const stepBack = useCallback(async () => {
    const store = useSessionStore.getState()
    const step = (store.popupSession ?? store.session) as any
    if (!step?.can_undo || inFlight.current) {
      return
    }
    inFlight.current = true
    try {
      const result = await store.undo(1)
      if (useSessionStore.getState().isLifelineActive()) {
        handoverToLifeline()
        return
      }
      if (!hasRenderableStep(result)) {
        await settleAndRefresh()
      }
    } catch (err) {
      toast.error(formatErrorMessage(err))
    } finally {
      inFlight.current = false
    }
  }, [handoverToLifeline, settleAndRefresh, toast])

  const cancel = useCallback(async () => {
    if (mode === 'add_product') {
      returnFunnel()
      close()
      return
    }
    if (mode === null) {
      close()
      return
    }

    const store = useSessionStore.getState()
    const step = (store.popupSession ?? store.session) as any
    // Submitting a payment input would re-run the charge the customer is escaping.
    const neverSubmit = mode === 'checkout' || !!error || actionIntent === InputType.RebillItem
    const action = neverSubmit ? ({ kind: 'recover' } as const) : resolveCloseAction(step)

    if (action.kind === 'submit') {
      submitInput(action.input)
      return
    }

    close()
    emitProcessingMessages(null, null)
    try {
      if (!(await resyncPortalSession())) {
        returnFunnel()
        navigate('/portal', { replace: true })
      }
    } finally {
      clearProcessingMessages()
    }
  }, [actionIntent, close, error, mode, navigate, submitInput])

  const value = useMemo(
    () => ({
      mode,
      title,
      item,
      product,
      exchangeProduct,
      busy,
      error,
      retry,
      intent: actionIntent,
      startAction,
      openAddProduct,
      openCheckout,
      submitInput,
      submitData,
      submitCheckout,
      stepBack,
      cancel,
      close,
    }),
    [
      mode,
      title,
      item,
      product,
      exchangeProduct,
      busy,
      error,
      retry,
      actionIntent,
      startAction,
      openAddProduct,
      openCheckout,
      submitInput,
      submitData,
      submitCheckout,
      stepBack,
      cancel,
      close,
    ]
  )

  return <OmpActionContext.Provider value={value}>{children}</OmpActionContext.Provider>
}
